import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  defaultGreeting,
  REALTIME_VOICES,
  reservationSettingsSchema,
  type RealtimeVoice,
  type ReservationSettings,
  type RestaurantSettings
} from "@glowin/schemas";
import { api } from "../api.js";
import { ErrorBox } from "./MenuPage.js";
import { usePageTitle } from "../ui.js";
import {
  BagIcon,
  CalendarIcon,
  CheckIcon,
  ClockIcon,
  Hint,
  MessageIcon,
  RecordIcon,
  TimerIcon,
  VoiceIcon,
  WithIcon
} from "../icons.js";

/** Order + voice settings the phone agent uses at call time. */
export function SettingsPage({ bookingsAvailable = false }: { bookingsAvailable?: boolean } = {}) {
  usePageTitle("Settings");
  const { restaurantId = "" } = useParams();
  const qc = useQueryClient();
  const key = ["settings", restaurantId];

  const loaded = useQuery({ queryKey: key, queryFn: () => api.getSettings(restaurantId) });
  // Same query key the shell uses, so this is served from cache rather than refetched. Only
  // needed for the greeting placeholder, which shows exactly what callers hear by default.
  const profile = useQuery({
    queryKey: ["profile", restaurantId],
    queryFn: () => api.getProfile(restaurantId),
    retry: false
  });
  // Follows the restaurant's preferred language, so the hint is what callers actually hear.
  const defaultGreetingText = defaultGreeting(
    profile.data?.displayName ?? "us",
    profile.data?.preferredLanguage
  );
  const [draft, setDraft] = useState<RestaurantSettings>();
  /*
   * A restaurant whose settings were saved before bookings existed has no `reservations` at all. Its defaults
   * are filled in here, in the baseline as well as the draft, so the page does not open already "unsaved".
   */
  const baseline = useMemo(
    () =>
      loaded.data
        ? { ...loaded.data, reservations: reservationSettingsSchema.parse(loaded.data.reservations ?? {}) }
        : undefined,
    [loaded.data]
  );

  useEffect(() => {
    if (baseline) setDraft(baseline);
  }, [baseline]);

  const save = useMutation({
    mutationFn: (next: RestaurantSettings) => api.putSettings(restaurantId, next),
    onSuccess: () => void qc.invalidateQueries({ queryKey: key })
  });

  if (loaded.isLoading) return <p className="muted">Loading settings…</p>;
  if (loaded.error) return <ErrorBox error={loaded.error} />;
  if (!draft) return null;

  const dirty = JSON.stringify(draft) !== JSON.stringify(baseline);
  const patch = (p: Partial<RestaurantSettings>) => setDraft({ ...draft, ...p });
  const bookings = draft.reservations;
  const patchBookings = (p: Partial<ReservationSettings>) => patch({ reservations: { ...bookings, ...p } });
  const limit = (key: keyof Omit<ReservationSettings, "enabled">, label: string, min: number, max: number, hint?: string) => (
    <label className="field">
      <span>
        {label}
        {hint ? <Hint>{hint}</Hint> : null}
      </span>
      <input
        type="number"
        min={min}
        max={max}
        value={bookings[key]}
        onChange={(e) => patchBookings({ [key]: Number(e.target.value) })}
      />
    </label>
  );

  return (
    <div className="form-page">
      <div className="page-head">
        <h1>Settings</h1>
        <div className="page-actions">
          {dirty && <span className="muted">Unsaved changes</span>}
          <button className="primary" disabled={!dirty || save.isPending} onClick={() => save.mutate(draft)}>
            <WithIcon icon={<CheckIcon size={16} />}>{save.isPending ? "Saving…" : "Save"}</WithIcon>
          </button>
        </div>
      </div>

      {save.error ? <ErrorBox error={save.error} /> : null}
      {save.isSuccess && !dirty ? (
        <div className="notice">
          <WithIcon icon={<CheckIcon size={16} />}>Saved</WithIcon>
        </div>
      ) : null}

      <div className="card form-card">
        <label className="field">
          <span>
            <WithIcon icon={<ClockIcon size={15} />}>Pickup time (minutes)</WithIcon>
            <Hint>The agent says “ready in about {draft.prepTimeMinutes} minutes”.</Hint>
          </span>
          <input
            type="number"
            min={1}
            max={240}
            value={draft.prepTimeMinutes}
            onChange={(e) => patch({ prepTimeMinutes: Number(e.target.value) })}
          />
        </label>

        <label className="field">
          <span>
            <WithIcon icon={<BagIcon size={15} />}>Maximum items per order</WithIcon>
          </span>
          <input
            type="number"
            min={1}
            max={100}
            value={draft.maxItemsPerOrder}
            onChange={(e) => patch({ maxItemsPerOrder: Number(e.target.value) })}
          />
        </label>

        <label className="field">
          <span>
            <WithIcon icon={<MessageIcon size={15} />}>Greeting</WithIcon> <span className="muted">(optional)</span>
            <Hint>The first thing callers hear. Leave blank for the greeting shown. Keep it to one short sentence.</Hint>
          </span>
          <input
            value={draft.greeting ?? ""}
            maxLength={200}
            placeholder={defaultGreetingText}
            onChange={(e) => {
              // One spoken line: newlines would let this text become its own prompt
              // instruction, and a paragraph wastes time on every call.
              const g = e.target.value.replace(/[\r\n]+/g, " ");
              patch({ greeting: g.trim() === "" ? undefined : g });
            }}
          />
        </label>

        <label className="field">
          <span>
            <WithIcon icon={<VoiceIcon size={15} />}>Agent voice</WithIcon>
            <Hint>marin and cedar are the highest quality.</Hint>
          </span>
          <select value={draft.voice} onChange={(e) => patch({ voice: e.target.value as RealtimeVoice })}>
            {REALTIME_VOICES.map((v) => (
              <option key={v} value={v}>
                {v}
                {v === "marin" || v === "cedar" ? " (recommended)" : ""}
              </option>
            ))}
          </select>
        </label>

        <label className="field">
          <span>
            <WithIcon icon={<TimerIcon size={15} />}>Speaking speed: {draft.speechSpeed.toFixed(2)}×</WithIcon>
            <Hint>1.0× is normal. Higher feels brisker but can be harder to follow.</Hint>
          </span>
          <input
            type="range"
            min={0.25}
            max={1.5}
            step={0.05}
            value={draft.speechSpeed}
            onChange={(e) => patch({ speechSpeed: Number(e.target.value) })}
          />
        </label>

        {/* Off unless the restaurant turns it on: recording its customers is the restaurant's decision. Read
            as === true because a restaurant that saved settings before this existed has no value at all. */}
        <label className="checkbox">
          <input
            type="checkbox"
            checked={draft.recordCalls === true}
            onChange={(e) => patch({ recordCalls: e.target.checked })}
          />
          <span>
            <WithIcon icon={<RecordIcon size={15} />}>Record calls</WithIcon>
            <Hint>
              Play them back on the Calls page. Kept for 7 days. Callers already hear that the call may be
              recorded.
            </Hint>
          </span>
        </label>
      </div>

      {bookingsAvailable ? (
        <section className="card form-card" aria-labelledby="bookings-heading">
          <h2 id="bookings-heading">
            <WithIcon icon={<CalendarIcon size={16} />}>Table bookings</WithIcon>
          </h2>
          {/* Off unless the restaurant turns it on. Until then the agent has no booking tools and never mentions
              tables. */}
          <label className="checkbox">
            <input
              type="checkbox"
              checked={bookings.enabled}
              onChange={(e) => patchBookings({ enabled: e.target.checked })}
            />
            <span>
              Take table bookings by phone
              <Hint>
                The agent books against your hours and the limits below, reads the booking back before making it, and
                lets callers check or cancel theirs. Bookings appear on the Reservations tab.
              </Hint>
            </span>
          </label>
          {bookings.enabled ? (
            <>
              <div className="field-row">
                {limit("maxPartySize", "Largest party", 1, 50, "Bigger groups are offered a message or a transfer instead.")}
                {limit("maxGuestsPerSlot", "Guests per half hour", 1, 500, "Across every booking arriving in the same 30 minutes.")}
              </div>
              <div className="field-row">
                {limit("maxDaysAhead", "Book up to (days ahead)", 1, 180)}
                {limit("minNoticeMinutes", "Minimum notice (minutes)", 0, 1440)}
              </div>
              {limit(
                "lastSeatingMinutesBeforeClose",
                "Last seating (minutes before closing)",
                0,
                240,
                "No arrivals later than this. Bookings you add yourself are not held to these limits."
              )}
            </>
          ) : null}
        </section>
      ) : null}
    </div>
  );
}
